import {
  makeStyles,
  IconButton,
  Tooltip,
  CircularProgress,
  withStyles,
} from '@material-ui/core';
import PollIcon from '@material-ui/icons/Poll';
import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import mentorsAPI, { Mentor, MentorStatistics } from '../../api/mentors';
import { useResourceBundles } from '../../contexts/resource-bundles-context';
import { getRoutePath, Pages } from '../../router/constants';
import { COLORS } from '../../theme/variables';
import { Link, Text } from '../common';

interface StatisticsIconProps {
  mentor: Mentor;
}

const STooltip = withStyles(() => ({
  tooltip: {
    width: 300,
    padding: 16,
    backgroundColor: COLORS.COLOR_ADDITIONAL_WHITE,
    boxShadow:
      '0px 2px 4px rgba(34, 91, 187, 0.2), 0px 4px 8px rgba(51, 126, 255, 0.12)',
  },
}))(Tooltip);

const useStyles = makeStyles(() => ({
  wrapper: {
    width: '100%',
  },
  loader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  listWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    maxHeight: 'calc(90vh)',
    overflowY: 'auto',
  },
  list: {
    display: 'grid',
    gridTemplateColumns: '1fr 100px',
    gap: 10,
    width: '100%',
  },
  name: {
    maxWidth: 220,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'left',
  },
}));

function StatisticsTooltip({ mentor }: StatisticsIconProps) {
  const classes = useStyles();
  const { rb } = useResourceBundles();
  const [stats, setStats] = useState<MentorStatistics | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const { attendancePercentage, attendanceColor } = useMemo(() => {
    if (!stats)
      return {
        attendancePercentage: 0,
        attendanceColor: COLORS.COLOR_RED_BASE,
      };

    const prePercentage = stats
      ? Math.round(
          (stats.numberOfSessionsAttended / stats.numberOfSessionsInPeriod) *
            100,
        )
      : 0;
    const attendancePercentage = isNaN(prePercentage) ? 0 : prePercentage;
    let attendanceColor = COLORS.COLOR_RED_BASE;

    switch (true) {
      case attendancePercentage >= 90:
        attendanceColor = COLORS.COLOR_GREEN_BASE;
        break;
      case attendancePercentage >= 75:
        attendanceColor = COLORS.COLOR_ADDITIONAL_ORANGE;
        break;
      default:
        attendanceColor = COLORS.COLOR_RED_BASE;
        break;
    }

    return { attendancePercentage, attendanceColor };
  }, [stats]);

  useEffect(() => {
    const fetchAttendants = async () => {
      try {
        setIsLoading(true);
        const statsRes = await mentorsAPI.getMentorStatistics(mentor.id);
        setStats(statsRes);
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAttendants();
  }, [mentor.id, setIsLoading]);

  return (
    <div className={classes.wrapper}>
      {isLoading ? (
        <div className={classes.loader}>
          <CircularProgress size={24} color='primary' />
        </div>
      ) : !stats ? (
        <Text variant='normal'>
          Something went wrong... Please try again later.
        </Text>
      ) : (
        <div className={classes.listWrapper}>
          <div className={classes.list}>
            <Text variant='normal' bold className={classes.name}>
              Attendance:
            </Text>
            <Text variant='normal' bold color={attendanceColor}>
              {attendancePercentage}%
            </Text>
            <Text variant='normal' className={classes.name}>
              Session Opportunities:
            </Text>
            <Text variant='normal'>{stats.numberOfSessionsInPeriod}</Text>
            <Text variant='normal' className={classes.name}>
              Session Attended:
            </Text>
            <Text variant='normal'>{stats.numberOfSessionsAttended}</Text>
            <Text variant='normal' className={classes.name}>
              Lead {rb('mentor-u')} Reports:
            </Text>
            <Text variant='normal'>{stats.leadMentorReportsSubmitted}</Text>
            <Text variant='normal' className={classes.name}>
              {rb('mentor-u')} Assessments:
            </Text>
            <Text variant='normal'>{stats.mentorAssessmentsSubmitted}</Text>
          </div>
        </div>
      )}
    </div>
  );
}

function StatisticsIcon({ mentor }: StatisticsIconProps) {
  const location = useLocation();

  return (
    <STooltip title={<StatisticsTooltip mentor={mentor} />}>
      <IconButton
        data-testid='statistics-icon'
        component={Link}
        to={{
          pathname: getRoutePath(Pages.MENTOR_STATS, {
            mentorId: mentor.id,
          }),
          state: {
            prevPath: `${location.pathname}${location.search}`,
          },
        }}>
        <PollIcon />
      </IconButton>
    </STooltip>
  );
}

export default StatisticsIcon;
